import type {
    PieChartGeometryConstantType,
    PieChartLabelsConstantType,
    PieChartSlicesConstantType,
    PieChartPointConstantType,
    PieChartHitConstantType,
    PieChartFitConstantType,
    PieChartGeometry,
    PieChartLabel,
    PieChartSlice,
    PieChartAngle,
    PieChartFit
} from "./type";
import {
    getChartSeriesColor,
    getChartTextWidth,
    getChartPattern
} from "../chart/util";

export const PIE_CHART_FULL_TURN = Math.PI * 2;

export const PIE_CHART_MIN_LABEL_SHARE = 0.05;

export const PIE_CHART_LABEL_SIDE_RATIO = 0.3;

export const PIE_CHART_CENTER_SHIFT = 0.35;

export const PIE_CHART_COLOR_COUNT = 8;

export const PIE_CHART_OTHER_KEY = "other";

export const PIE_CHART_LABEL_SAMPLE = "%100";

export const PIE_CHART_VALUE_STEPS = [
    "headlineSmallSize",
    "titleLargeSize",
    "titleMediumSize",
    "titleSmallSize"
] as const;

export const PIE_CHART_MAX_MEMBERS = 5;

export const getPieChartSlices = ({
    categoryKey,
    isPatterned,
    otherTitle,
    maxSlices,
    valueKey,
    colors,
    config,
    data
}: PieChartSlicesConstantType): Array<PieChartSlice> => {
    const entries = data.map((row) => ({
        value: Math.max(0, Number(row[valueKey]) || 0),
        key: String(row[categoryKey])
    })).filter((entry) => entry.value > 0);

    const keptCount = Math.max(1, maxSlices - 1);

    const isOverflowing = entries.length > Math.max(1, maxSlices);

    const kept = isOverflowing ? [
        ...entries
    ].sort((first, second) => second.value - first.value).slice(0, keptCount) : entries;

    const folded = entries.filter((entry) => !kept.includes(entry));

    const ordered = entries.filter((entry) => kept.includes(entry));

    const getTitle = (key: string) => config?.[key]?.title ?? key;

    const slices = ordered.map((entry, index): PieChartSlice => {
        const colorIndex = config?.[entry.key]?.colorIndex ?? ((index % PIE_CHART_COLOR_COUNT) + 1) as 1;

        return {
            ...getChartSeriesColor({
                colorIndex,
                colors
            }),
            title: getTitle(entry.key),
            pattern: getChartPattern({
                isPatterned,
                colorIndex
            }),
            value: entry.value,
            isOther: false,
            key: entry.key,
            members: [],
            colorIndex
        };
    });

    if(folded.length) {
        slices.push({
            ...getChartSeriesColor({
                colorIndex: "muted",
                colors
            }),
            members: [
                ...folded
            ].sort((first, second) => second.value - first.value).map((entry) => ({
                title: getTitle(entry.key),
                value: entry.value
            })),
            value: folded.reduce((sum, entry) => sum + entry.value, 0),
            key: PIE_CHART_OTHER_KEY,
            colorIndex: "muted",
            title: otherTitle,
            pattern: "none",
            isOther: true
        });
    }

    return slices;
};

export const getPieChartAngles = (values: Array<number>): Array<PieChartAngle> => {
    const total = values.reduce((sum, value) => sum + Math.max(0, value), 0);

    let cursor = 0;

    return values.map((value): PieChartAngle => {
        const share = total > 0 ? Math.max(0, value) / total : 0;

        const start = cursor;

        cursor += share * PIE_CHART_FULL_TURN;

        return {
            mid: (start + cursor) / 2,
            end: cursor,
            share,
            start
        };
    });
};

export const getPieChartGeometry = ({
    measures,
    reserveX,
    reserve,
    variant,
    height,
    width
}: PieChartGeometryConstantType): PieChartGeometry => {
    const outer = Math.max(0, Math.min(width / 2 - reserveX, height / 2 - reserve));

    return {
        inner: variant === "donut" ? outer * (1 - measures.ratio) : 0,
        cy: height / 2,
        cx: width / 2,
        outer
    };
};

export const getPieChartPoint = ({
    geometry,
    radius,
    isRTL,
    angle
}: PieChartPointConstantType) => {
    const sign = isRTL ? -1 : 1;

    return {
        x: geometry.cx + sign * radius * Math.sin(angle),
        y: geometry.cy - radius * Math.cos(angle)
    };
};

export const getPieChartHitAngle = ({
    geometry,
    isRTL,
    lift,
    x,
    y
}: PieChartHitConstantType): number | undefined => {
    const dx = x - geometry.cx;

    const dy = y - geometry.cy;

    const distance = Math.hypot(dx, dy);

    if(distance < geometry.inner || distance > geometry.outer + lift) {
        return undefined;
    }

    const angle = Math.atan2(dy, dx) + Math.PI / 2;

    return ((isRTL ? -angle : angle) % PIE_CHART_FULL_TURN + PIE_CHART_FULL_TURN) % PIE_CHART_FULL_TURN;
};

export const getPieChartLabels = ({
    labelTypography,
    geometry,
    getText,
    angles,
    height,
    offset,
    isRTL,
    width
}: PieChartLabelsConstantType): Array<PieChartLabel> => {
    const boxes: Array<[number, number, number, number]> = [];

    const labels: Array<PieChartLabel> = [];

    const sign = isRTL ? -1 : 1;

    angles.forEach((angle, index) => {
        if(angle.share < PIE_CHART_MIN_LABEL_SHARE) {
            return;
        }

        const point = getPieChartPoint({
            radius: geometry.outer + offset,
            angle: angle.mid,
            geometry,
            isRTL
        });

        const text = getText(angle.share);

        const horizontal = sign * Math.sin(angle.mid);

        const anchor = horizontal > PIE_CHART_LABEL_SIDE_RATIO ? "start" : horizontal < -PIE_CHART_LABEL_SIDE_RATIO ? "end" : "middle";

        const textWidth = getChartTextWidth({
            fontSize: labelTypography.fontSize,
            text
        });

        const centerY = point.y - Math.cos(angle.mid) * labelTypography.lineHeight / 2;

        const left = anchor === "start" ? point.x : anchor === "end" ? point.x - textWidth : point.x - textWidth / 2;

        const box: [number, number, number, number] = [
            left,
            centerY - labelTypography.lineHeight / 2,
            left + textWidth,
            centerY + labelTypography.lineHeight / 2
        ];

        const isClashing = boxes.some((other) => box[0] < other[2] && box[2] > other[0] && box[1] < other[3] && box[3] > other[1]);

        if(isClashing || box[0] < 0 || box[2] > width || box[1] < 0 || box[3] > height) {
            return;
        }

        boxes.push(box);

        labels.push({
            y: centerY + labelTypography.fontSize * PIE_CHART_CENTER_SHIFT,
            x: point.x,
            anchor,
            index,
            text
        });
    });

    return labels;
};

export const getPieChartFit = ({
    typography,
    titleText,
    valueText,
    geometry,
    measures
}: PieChartFitConstantType): PieChartFit | undefined => {
    const room = geometry.inner - measures.centerClearance;

    if(room <= 0) {
        return undefined;
    }

    const labelTypography = measures.labelTypography;

    const titleWidth = getChartTextWidth({
        fontSize: labelTypography.fontSize,
        text: titleText
    });

    const candidates = [
        true,
        false
    ].filter((isTitleVisible) => isTitleVisible ? Boolean(titleText) : true).flatMap((isTitleVisible) => PIE_CHART_VALUE_STEPS.map((step) => ({
        valueTypography: typography[step],
        isTitleVisible
    })));

    return candidates.map((candidate): PieChartFit => ({
        labelTypography,
        ...candidate
    })).find((candidate) => {
        const valueWidth = getChartTextWidth({
            fontSize: candidate.valueTypography.fontSize,
            text: valueText
        });

        const width = candidate.isTitleVisible ? Math.max(valueWidth, titleWidth) : valueWidth;

        const height = candidate.valueTypography.lineHeight + (candidate.isTitleVisible ? labelTypography.lineHeight : 0);

        return Math.hypot(width / 2, height / 2) <= room;
    });
};
